import { type EnvironmentId, type ThreadId } from "@t3tools/contracts";
import { ChevronRightIcon } from "lucide-react";
import { useMemo } from "react";
import { scopeThreadRef } from "@t3tools/client-runtime";
import { useShallow } from "zustand/react/shallow";
import { useTranslation } from "react-i18next";

import { t } from "~/i18n";
import { LiveText } from "./chat/LiveText";
import { taskStatusLabel } from "./chat/taskStatusLabels";
import {
  buildSubAgentTaskPromptByChildThreadId,
  formatDuration,
  formatElapsed,
} from "../session-logic";
import {
  selectEnvironmentState,
  selectSubAgentChildStatus,
  selectSubAgentChildTurn,
  selectSubAgentChildren,
  useStore,
} from "../store";
import { useSubAgentPanelStore } from "../subAgentPanelStore";
import { type ThreadShell } from "../types";

export function SubAgentListView({
  environmentId,
  threadId,
}: {
  environmentId: EnvironmentId;
  threadId: ThreadId;
}) {
  const children = useStore(
    useShallow((state) => selectSubAgentChildren(state, scopeThreadRef(environmentId, threadId))),
  );
  const parentActivityIds = useStore(
    (state) => selectEnvironmentState(state, environmentId).activityIdsByThreadId[threadId],
  );
  const parentActivityById = useStore(
    (state) => selectEnvironmentState(state, environmentId).activityByThreadId[threadId],
  );
  const promptByChildThreadId = useMemo(
    () =>
      buildSubAgentTaskPromptByChildThreadId(
        (parentActivityIds ?? []).flatMap((activityId) => {
          const activity = parentActivityById?.[activityId];
          return activity ? [activity] : [];
        }),
      ),
    [parentActivityById, parentActivityIds],
  );

  if (children.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <p className="text-center text-sm text-muted-foreground/30">
          {t("common/thisThreadHasNot_xs7xem")}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-2">
      <ul className="flex flex-col gap-1">
        {children.map((child) => (
          <li key={child.id}>
            <SubAgentListRow child={child} prompt={promptByChildThreadId.get(child.id)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function SubAgentListRow({ child, prompt }: { child: ThreadShell; prompt: string | undefined }) {
  const { i18n } = useTranslation();
  const push = useSubAgentPanelStore((state) => state.push);
  const childRef = scopeThreadRef(child.environmentId, child.id);
  const status = useStore((state) => selectSubAgentChildStatus(state, childRef));
  const turn = useStore((state) => selectSubAgentChildTurn(state, childRef));

  return (
    <button
      type="button"
      onClick={() =>
        push({
          kind: "thread",
          environmentId: child.environmentId,
          threadId: child.id,
          title: child.title,
          ...(prompt ? { prompt } : {}),
        })
      }
      className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent"
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-foreground">{child.title}</span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {status ? `${taskStatusLabel(i18n, status)} · ` : ""}
          <SubAgentDuration
            startedAt={turn?.startedAt ?? null}
            completedAt={status === "running" ? null : (turn?.completedAt ?? null)}
          />
        </span>
      </span>
      <ChevronRightIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground/50" />
    </button>
  );
}

function SubAgentDuration({
  startedAt,
  completedAt,
}: {
  startedAt: string | null;
  completedAt: string | null;
}) {
  if (!startedAt) {
    return null;
  }
  if (completedAt) {
    return <>{formatElapsed(startedAt, completedAt)}</>;
  }
  const startedAtMs = Date.parse(startedAt);
  if (!Number.isFinite(startedAtMs)) {
    return null;
  }
  return <LiveText compute={() => formatDuration(Date.now() - startedAtMs)} />;
}
